import { type QueryKey, useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useDeleteBuild, useUpdateBuild } from '@/features/builds/hooks/builds';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { queryJobRuns } from '@/features/jobs/data/jobs';
import { buildJobDetailPath, buildJobRunDetailPath } from '@/features/jobs/utils/jobPaths';
import {
  buildJobRunColgroup,
  JobRunNestedHeader,
  nestedJobRunSkeletonColumns,
  type SortableJobRunField,
} from '@/features/pipelines/components/PipelineTableV2NestedLayout';
import { buildStageRunJobRunFilters } from '@/features/pipelines/components/PipelineTableV2NestedRuns.helpers';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import { TableV2NestedEmptyState } from '@/features/shared/components/tableSystem/TableV2NestedEmptyState';
import { TableV2NestedFrame } from '@/features/shared/components/tableSystem/TableV2NestedFrame';
import {
  buildTableV2NestedBodyRowSx,
  queryAllTableV2NestedRows,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
  type TableV2NestedTableDepth,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2DateTimeCell,
  TableV2NameCell,
  TableV2NestedCollapse,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2SkeletonRow } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { useNotifications } from '@/providers/notificationHooks';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { buildDateRangeFilters, buildEqFilterValue } from '@/ui/composites/grid/gridFilters';
import {
  Box,
  formatStatusLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/ui/primitives';
import type { TagColorByName } from '@/features/shared/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { BuildDto, QueryResult } from '@flowqa/shared';

const defaultJobRunSort: ApiSort = { field: 'startedAt', direction: 'desc' };

const JobRunNestedRow = ({
  depth,
  isLastVisibleRow,
  row,
  showJobLink,
  tagColorByName,
}: {
  depth: TableV2NestedTableDepth;
  isLastVisibleRow: boolean;
  row: BuildDto;
  showJobLink: boolean;
  tagColorByName: TagColorByName;
}): ReactElement => {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const updateBuild = useUpdateBuild();
  const deleteBuild = useDeleteBuild();
  const runNumber = resolveBuildRunNumber(row.buildKey ?? null);
  const jobName = row.jobName ?? row.id;
  const rowName = runNumber === null ? (row.buildKey ?? row.id) : `#${String(runNumber)}`;
  const jobPathParams = {
    pipelineId: row.pipelineId ?? null,
    stageName: row.stageName ?? null,
    jobName,
  };
  const jobHref = buildJobDetailPath(jobPathParams);
  const jobRunHref = buildJobRunDetailPath({
    ...jobPathParams,
    jobRunId: row.id,
  });
  const durationMs =
    row.startedAt && row.finishedAt ? row.finishedAt.getTime() - row.startedAt.getTime() : null;
  const editEditor = useDetailEditEditor<BuildDto['status']>({
    projectId: row.projectId,
    successMessage: 'Job run updated',
    saveEntity: async ({
      tags,
      status,
      timestamp,
      secondaryTimestamp,
      durationMs: nextDurationMs,
    }) => {
      const startedAt = timestamp ? timestamp.toISOString() : null;
      const finishedAt =
        secondaryTimestamp !== undefined
          ? (secondaryTimestamp?.toISOString() ?? null)
          : timestamp && nextDurationMs !== null && nextDurationMs !== undefined
            ? new Date(timestamp.getTime() + nextDurationMs).toISOString()
            : null;
      await updateBuild.mutateAsync({
        id: row.id,
        tags,
        status,
        startedAt,
        finishedAt,
      });
    },
  });

  return (
    <>
      <TableRow sx={buildTableV2NestedBodyRowSx({ depth, hasRenderedDivider: !isLastVisibleRow })}>
        <TableCell>
          <TableV2NameCell
            href={showJobLink ? jobHref : jobRunHref}
            primary={showJobLink ? jobName : rowName}
            secondary={showJobLink ? rowName : null}
            secondaryHref={showJobLink ? jobRunHref : null}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <TableV2DurationCell value={durationMs} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.startedAt ?? row.createdAt} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={rowName}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Job Run')}
            deleteActionLabel={getEntityActionLabel('delete', 'Job Run')}
            rowTags={row.tags ?? []}
            onEdit={() => {
              setEditOpen(true);
            }}
            onDelete={() => {
              setDeleteOpen(true);
            }}
            disableEdit={editEditor.isPending || deleteBuild.isPending}
            disableDelete={deleteBuild.isPending}
          />
        </TableCell>
      </TableRow>
      <DetailEditEntityDialog
        open={editOpen}
        entityLabel="Job Run"
        entityName={rowName}
        projectId={row.projectId}
        tags={row.tags ?? []}
        status={row.status}
        statusOptions={[
          { value: 'queued', label: formatStatusLabel('queued') },
          { value: 'running', label: formatStatusLabel('running') },
          { value: 'completed', label: formatStatusLabel('completed') },
          { value: 'failed', label: formatStatusLabel('failed') },
        ]}
        timestamp={row.startedAt ?? null}
        secondaryTimestamp={row.finishedAt ?? null}
        durationMs={durationMs}
        timestampLabel="Started"
        secondaryTimestampLabel="Finished"
        durationLabel="Duration"
        durationInputMode="timeParts"
        loading={editEditor.isPending}
        cascadeCopy="Tags will be applied only to this job run."
        onClose={() => {
          if (editEditor.isPending) return;
          setEditOpen(false);
        }}
        onSave={async ({
          tags,
          colors,
          status,
          timestamp,
          secondaryTimestamp,
          durationMs: nextDurationMs,
        }) => {
          await editEditor.save({
            tags,
            colors,
            status,
            timestamp,
            secondaryTimestamp,
            durationMs: nextDurationMs,
          });
          setEditOpen(false);
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        title={getDialogMessage('destructive.delete.title', { entity: 'job' })}
        description={getDialogMessage('destructive.delete.simpleDescription', { entity: 'job' })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'job' })}
        confirmColor="red"
        loading={deleteBuild.isPending}
        onClose={() => {
          if (deleteBuild.isPending) return;
          setDeleteOpen(false);
        }}
        onConfirm={() => {
          deleteBuild.mutate(
            { id: row.id, projectId: row.projectId },
            {
              onSuccess: () => {
                notify({
                  message: getToastMessage('entity.deleted', {
                    entity: 'job run',
                    name: rowName,
                  }),
                  tone: 'green',
                });
                setDeleteOpen(false);
              },
              onError: (error) => {
                notify({ message: toErrorMessage(error), tone: 'red' });
              },
            },
          );
        }}
      />
    </>
  );
};

const JobRunsNestedTable = ({
  ariaLabel,
  childCount,
  depth,
  emptyText,
  expanded,
  nameHeaderLabel,
  showJobLink,
  tagColorByName,
  buildQueryKey,
  queryFn,
}: {
  ariaLabel: string;
  childCount: number;
  depth: TableV2NestedTableDepth;
  emptyText: string;
  expanded: boolean;
  nameHeaderLabel: string;
  showJobLink: boolean;
  tagColorByName: TagColorByName;
  buildQueryKey: (sort: ApiSort) => QueryKey;
  queryFn: (sort: ApiSort) => Promise<QueryResult<BuildDto>>;
}): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultJobRunSort);
  const handleSortChange = (field: SortableJobRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const query = useQuery({
    queryKey: buildQueryKey(sort),
    enabled: expanded,
    queryFn: async () => await queryFn(sort),
    staleTime: 30_000,
  });
  const header = (
    <JobRunNestedHeader
      nameHeaderLabel={nameHeaderLabel}
      sort={sort}
      onSortChange={handleSortChange}
    />
  );
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <TableV2NestedFrame depth={depth}>
            <Table aria-label={`${ariaLabel} loading`} sx={tableV2NestedDepthSx[depth].table}>
              {buildJobRunColgroup()}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`${ariaLabel}-nested-skeleton-${String(index)}`}
                    columns={nestedJobRunSkeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : query.data && query.data.items.length > 0 ? (
          <TableV2NestedFrame depth={depth}>
            <Table aria-label={ariaLabel} sx={tableV2NestedDepthSx[depth].table}>
              {buildJobRunColgroup()}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((jobRun, index, jobRuns) => (
                  <JobRunNestedRow
                    key={jobRun.id}
                    depth={depth}
                    isLastVisibleRow={index === jobRuns.length - 1}
                    row={jobRun}
                    showJobLink={showJobLink}
                    tagColorByName={tagColorByName}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : (
          <TableV2NestedEmptyState
            ariaLabel={ariaLabel}
            colgroup={buildJobRunColgroup()}
            colSpan={6}
            depth={depth}
            header={header}
            text={emptyText}
          />
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};

export const JobExpandedRuns = ({
  childCount,
  expanded,
  jobName,
  jobRunsEndAt,
  jobRunsStartAt,
  organisationId,
  pipelineId,
  projectId,
  stageName,
  tagColorByName,
}: {
  childCount: number;
  expanded: boolean;
  jobName: string;
  jobRunsEndAt: string | null;
  jobRunsStartAt: string | null;
  organisationId: string | null;
  pipelineId: string | null;
  projectId: string;
  stageName: string | null;
  tagColorByName: TagColorByName;
}): ReactElement => (
  <JobRunsNestedTable
    ariaLabel="Job runs"
    childCount={childCount}
    depth={1}
    emptyText="No data"
    expanded={expanded}
    nameHeaderLabel="Run"
    showJobLink={false}
    tagColorByName={tagColorByName}
    buildQueryKey={(sort) =>
      queryKeys.dataGrid(
        gridPaths.jobRuns,
        'v2-nested-job-runs-by-job',
        organisationId,
        projectId,
        pipelineId,
        stageName,
        jobName,
        jobRunsStartAt,
        jobRunsEndAt,
        sort.field,
        sort.direction,
      )
    }
    queryFn={async (sort) =>
      await queryAllTableV2NestedRows(
        {
          page: 1,
          pageSize: 1,
          sorts: [sort],
          filters: [
            ...buildEqFilterValue('pipelineId', pipelineId),
            ...buildEqFilterValue('stageName', stageName),
            ...buildEqFilterValue('jobName', jobName),
            ...buildDateRangeFilters('startedAt', jobRunsStartAt, jobRunsEndAt),
          ],
        },
        async (nextInput: GridQueryInput): Promise<QueryResult<BuildDto>> =>
          await queryJobRuns(projectId, organisationId, nextInput),
      )
    }
  />
);

export const StageRunExpandedJobRuns = ({
  childCount,
  depth,
  expanded,
  organisationId,
  pipelineExecutionId,
  projectId,
  stageName,
  stageExecutionId,
  tagColorByName,
}: {
  childCount: number;
  depth: TableV2NestedTableDepth;
  expanded: boolean;
  organisationId: string | null;
  pipelineExecutionId: string;
  projectId: string;
  stageName: string;
  stageExecutionId: string;
  tagColorByName: TagColorByName;
}): ReactElement => (
  <JobRunsNestedTable
    ariaLabel="Job runs"
    childCount={childCount}
    depth={depth}
    emptyText="No data"
    expanded={expanded}
    nameHeaderLabel="Job Run"
    showJobLink
    tagColorByName={tagColorByName}
    buildQueryKey={(sort) =>
      queryKeys.dataGrid(
        gridPaths.jobRuns,
        'v2-nested-job-runs',
        organisationId,
        projectId,
        stageExecutionId,
        pipelineExecutionId,
        stageName,
        sort.field,
        sort.direction,
      )
    }
    queryFn={async (sort) =>
      await queryAllTableV2NestedRows(
        {
          page: 1,
          pageSize: 1,
          sorts: [sort],
          filters: buildStageRunJobRunFilters({ pipelineExecutionId, stageName }),
        },
        async (nextInput: GridQueryInput): Promise<QueryResult<BuildDto>> =>
          await queryJobRuns(projectId, organisationId, nextInput),
      )
    }
  />
);
